import { useInViewOnce } from "../../hooks/useInViewOnce";
import { cn } from "../../lib/utils";
import { Hammer, Laptop, Music, Store } from "lucide-react";

type CharityService = {
  id: string;
  title: string;
  description: string;
  Icon: typeof Laptop;
};

const services: CharityService[] = [
  {
    id: "corel-draw",
    title: "Curso de Corel Draw",
    description:
      "Formação prática para abrir portas: aprender, criar e transformar habilidade em oportunidade real.",
    Icon: Laptop,
  },
  {
    id: "violao",
    title: "Curso de Violão",
    description:
      "A música como caminho de esperança: disciplina, expressão e novas histórias sendo escritas nota por nota.",
    Icon: Music,
  },
  {
    id: "construcao-reforma",
    title: "Construção e reforma",
    description:
      "Mãos que servem para restaurar dignidade — um lar mais seguro para recomeçar com paz e cuidado.",
    Icon: Hammer,
  },
  {
    id: "empreendimentos-familiares",
    title: "Incentivo para empreendimentos familiares",
    description:
      "Apoio para pequenos começos: fortalecer renda, autonomia e sonhos que sustentam toda a casa.",
    Icon: Store,
  },
];

function CharityServiceCard({ service, index }: { service: CharityService; index: number }) {
  const { ref, inView } = useInViewOnce<HTMLDivElement>({ threshold: 0.2 });

  return (
    <article
      ref={ref}
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-6 shadow-sm",
        "transition-all duration-300 will-change-transform",
        "hover:-translate-y-1 hover:bg-card hover:shadow-md",
        !inView && "opacity-0 translate-y-3",
        inView && "opacity-100 translate-y-0"
      )}
      style={{ transitionDelay: `${Math.min(index * 90, 240)}ms` }}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_hsl(var(--primary)/0.10),_transparent_55%)] opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      <div className="relative flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary transition-colors duration-300 group-hover:bg-primary/15">
          <service.Icon className="h-6 w-6" aria-hidden="true" />
        </div>

        <div className="min-w-0">
          <h3 className="text-base font-semibold text-foreground">{service.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{service.description}</p>
        </div>
      </div>
    </article>
  );
}

export default function AscenderCharityServicesOffered() {
  return (
    <section className="mt-12">
      <header className="mb-6 space-y-2">
        <h2 className="text-xl font-semibold text-foreground">Serviços de Caridade Oferecidos</h2>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Em cada ação, procuramos unir cuidado prático e presença amorosa — servindo com excelência e anunciando esperança.
          Estes são alguns dos serviços que, hoje, fazem parte do que o Projeto Ascender oferece.
        </p>
      </header>

      <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap">
        {services.map((service, index) => (
          <div
            key={service.id}
            className={cn(
              "w-full",
              // Layout propositalmente menos rígido (varia a largura dos cards em telas grandes).
              index % 4 === 0 && "lg:basis-[42%]",
              index % 4 === 1 && "lg:basis-[52%]",
              index % 4 === 2 && "lg:basis-[48%]",
              index % 4 === 3 && "lg:basis-[46%]"
            )}
          >
            <CharityServiceCard service={service} index={index} />
          </div>
        ))}
      </div>
    </section>
  );
}
